import { zodResolver } from '@hookform/resolvers/zod'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import { AppRoutes } from '@/constants/appRoutes'
import { useAppSelector } from '@/hooks/redux'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import {
  useOtpVerificationPhoneMutation,
  useSendOtpPhoneMutation,
} from '@/store/api/Auth/Auth.query'
import { selectCredentials } from '@/store/api/SignUp/SignUp.selector'
import { useUserInfoQuery } from '@/store/api/Users/Users.query'
import type { emailValidationType } from '@/utils/schemas'
import { emailValidationSchema } from '@/utils/schemas'

import Env from '../../../../env/env'
import { WeScreen } from '@/components/WeScreen/WeScreen'

export const SettingsPhoneVerification = () => {
  const isDev = Env.NODE_ENV === 'development' || Env.NODE_ENV === 'local'
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: userData, isSuccess, isLoading } = useUserInfoQuery()
  const userPhone = useAppSelector(selectCredentials).phoneNumber

  const userEmail = userData?.email || ''

  const [sendOtpPhone, { isLoading: isLoadingSendPhone }] =
    useSendOtpPhoneMutation()
  const [verifyOtpPhone, { isLoading: isLoadingVerifyPhone }] =
    useOtpVerificationPhoneMutation()

  const [timeLeft, setTimeLeft] = useState<number>(0)
  const [canResend, setCanResend] = useState<boolean>(true)

  const calculateTimeLeft = (timestamp: string) => {
    const nowInSeconds = new Date().getTime() / 1000
    const requestTimeInSeconds = new Date(timestamp).getTime() / 1000
    const secondsTranscurred = Math.floor(nowInSeconds - requestTimeInSeconds)
    const timeLeft = Math.max(0, 180 - secondsTranscurred)
    return timeLeft
  }

  const finalUserPhone = userPhone.replace(/[+\s]/g, '')

  const sendSMS = async () => {
    if (!userData) return

    try {
      const response = await sendOtpPhone({
        phone: finalUserPhone,
        userId: userData.id,
      }).unwrap()

      const timeLeft = calculateTimeLeft(response.timestamp)
      setTimeLeft(timeLeft)
      setCanResend(false)

      toast.success(t('feedback.toast.smsOTPCode'))
    } catch (e) {
      catchWithSentry(e)
      toast.error(t('feedback.toast.errorSmsOTP'))
    }
  }

  const verifyPhone = async (otp: number) => {
    try {
      await verifyOtpPhone({
        phone: finalUserPhone,
        email: userEmail,
        otp,
      }).unwrap()
      toast.success(t('feedback.toast.phoneVerified'))
      // Always navigate back to Settings
      navigate(AppRoutes.account)
    } catch (e) {
      catchWithSentry(e)
      toast.error(t('feedback.toast.invalidOTP'))
    }
  }

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null
    if (timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prevTime: number) => {
          if (prevTime <= 1) {
            setCanResend(true)
            return 0
          }
          return prevTime - 1
        })
      }, 1000)
    }

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [timeLeft])

  useEffect(() => {
    if (userData && !isDev) sendSMS()
  }, [isSuccess, isDev])

  const form = useForm<emailValidationType>({
    resolver: zodResolver(emailValidationSchema),
    defaultValues: {
      pin: '',
    },
  })

  const {
    handleSubmit,
    control,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { errors = {} },
  } = form

  const onSubmit = (data: any) => {
    const otp = parseInt(data.pin, 10)
    verifyPhone(otp)
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const pasteData = event.clipboardData.getData('text')

    if (pasteData.length === 6) {
      const inputs = event.currentTarget.form?.elements
      if (inputs) {
        pasteData.split('').forEach((char, index) => {
          ;(inputs[index] as HTMLInputElement).value = char
          ;(inputs[index] as HTMLInputElement).focus()
        })
      }
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <WeScreen useNextButton={false}>
      <div className="flex flex-col gap-8 w-full items-center h-full justify-center">
        <div className="flex flex-col gap-5 w-full items-center">
          <p className="text-[40px] font-medium tracking-wide w-[456px] max-sm:w-[310px] text-center text-neutralColor">
            {t('signUp.phoneVerification.title')}
          </p>
          <p className="text-base w-[456px] max-sm:w-[310px] font-medium  text-neutralColor80 tracking-tight text-pretty text-center">
            {t('signUp.phoneVerification.weSentTo')} {userPhone}&nbsp;
            {t('signUp.phoneVerification.pleaseCheckYourPhone')}
          </p>
        </div>

        <Form {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-6 w-1/3 max-sm:w-[310px] items-center"
          >
            <FormField
              control={control}
              name="pin"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <InputOTP
                      maxLength={6}
                      {...field}
                      disabled={isLoadingSendPhone || isLoadingVerifyPhone}
                      onPaste={() => handlePaste}
                    >
                      <InputOTPGroup className="flex gap-6 w-full max-sm:gap-2">
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </FormControl>
                </FormItem>
              )}
            />
            <p className="text-neutralColor80 text-sm font-normal tracking-tight">
              {t('signUp.phoneVerification.typeThe6DigitCode')}
            </p>

            <Button
              disabled={!form.formState.isValid || isLoadingVerifyPhone}
              type="submit"
              className="py-3 px-5 font-medium tracking-widest h-11 w-full rounded-3xl"
            >
              {t('signUp.phoneVerification.continue')}
            </Button>
            <Button
              variant={'ghost'}
              onClick={async (e) => {
                e.preventDefault()
                if (canResend && userData) {
                  sendSMS()
                }
              }}
              disabled={!canResend || isLoadingSendPhone || isDev || isLoading}
              className="w-min p-0 h-5 border-none"
            >
              <p
                className={`text-sm font-medium tracking-tight bg-white ${
                  canResend ? 'text-dynamicBlue' : 'text-gray-400'
                }`}
              >
                {canResend
                  ? t('signUp.phoneVerification.clickToResend')
                  : t('signUp.phoneVerification.resendIn', {
                      time: formatTime(timeLeft),
                    })}
              </p>
            </Button>
            <Button
              variant={'ghost'}
              onClick={() => navigate(AppRoutes.settingsVerifyPhone)}
              className="w-min p-0 h-5 border-none text-dynamicBlue"
            >
              {t('signUp.phoneVerification.editPhoneNumber')}
            </Button>
          </form>
        </Form>
        <p className="text-neutralColor80 text-sm font-normal text-center">
          {t('signUp.phoneVerification.waitingTooLong')}
          <span
            className="text-dynamicBlue cursor-pointer"
            onClick={() => navigate(AppRoutes.contactUs)}
          >
            {t('signUp.phoneVerification.contactUs')}
          </span>
        </p>
      </div>
    </WeScreen>
  )
}
